import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { appendExtrapolationNodeToPipeline, editExtrapolationNode, reconfigureNode } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './ExtrapolationConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const DEFAULT_WIND_COL = 'Scaled_Wind O/P';
const DEFAULT_SOLAR_COL = 'Scaled_Solar O/P (P75)';
const DEFAULT_CONS_COL = 'Hourly Consumption (kWh)';

const normalizeOptionalDerivedColumn = (value, fallbackLabel = null) => {
  if (value === undefined || value === null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (fallbackLabel && trimmed.toLowerCase() === fallbackLabel.toLowerCase()) return '';
  return String(value);
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const ExtrapolationConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [years, setYears] = useState(initialData.years || '');
  const [bankableYears, setBankableYears] = useState(initialData.bankableYears || '');
  const [windDepPerc, setWindDepPerc] = useState(initialData.windDepPerc || '');
  const [solarDepPerc, setSolarDepPerc] = useState(initialData.solarDepPerc || '');
  const [bankingCharge, setBankingCharge] = useState(initialData.bankingCharge || '');
  const [totWindGenCol, setTotWindGenCol] = useState(
    normalizeOptionalDerivedColumn(initialData.totWindGenCol, DEFAULT_WIND_COL)
  );
  const [totSolarGenCol, setTotSolarGenCol] = useState(
    normalizeOptionalDerivedColumn(initialData.totSolarGenCol, DEFAULT_SOLAR_COL)
  );
  const [consAppPerc, setConsAppPerc] = useState(initialData.consAppPerc || '');
  const [tConsCol, setTConsCol] = useState(
    normalizeOptionalDerivedColumn(initialData.tConsCol, DEFAULT_CONS_COL)
  );
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReconfiguring, setIsReconfiguring] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're reconfiguring or editing
  useEffect(() => {
    setIsReconfiguring(initialData.isReconfiguring || false);
    setIsEditing(initialData.isEditing || false);
  }, [initialData.isReconfiguring, initialData.isEditing]);
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      // Make sure we have valid years
      if (!years || isNaN(years) || parseInt(years) <= 0) {
        setSubmitError({ title: 'Invalid years', message: 'Please enter a valid number of years (positive numeric value).' });
        return;
      }

      // Make sure we have valid bankable years
      if (!bankableYears || isNaN(bankableYears) || parseInt(bankableYears) < 0) {
        setSubmitError({ title: 'Invalid bankable years', message: 'Please enter a valid number of bankable years (non-negative numeric value).' });
        return;
      }
      
      // Make sure we have valid Wind depreciation percentage
      if (!windDepPerc || isNaN(windDepPerc) || parseFloat(windDepPerc) < 0) {
        setSubmitError({ title: 'Invalid wind depreciation', message: 'Please enter a valid Wind depreciation percentage (non-negative numeric value).' });
        return;
      }
      
      // Make sure we have valid Solar depreciation percentage
      if (!solarDepPerc || isNaN(solarDepPerc) || parseFloat(solarDepPerc) < 0) {
        setSubmitError({ title: 'Invalid solar depreciation', message: 'Please enter a valid Solar depreciation percentage (non-negative numeric value).' });
        return;
      }
      
      // Make sure we have valid Banking charge percentage
      if (!bankingCharge || isNaN(bankingCharge) || parseFloat(bankingCharge) < 0) {
        setSubmitError({ title: 'Invalid banking charge', message: 'Please enter a valid Banking charge percentage (non-negative numeric value).' });
        return;
      }
      
      // Make sure we have valid Consumption appreciation percentage
      if (!consAppPerc || isNaN(consAppPerc) || parseFloat(consAppPerc) < 0) {
        setSubmitError({ title: 'Invalid appreciation %', message: 'Please enter a valid Consumption appreciation percentage (non-negative numeric value).' });
        return;
      }
      
      // Columns are optional now
      
      // Get the input node name and position from initialData
      const inputNodeName = initialData.inputNodeName;
      const position = initialData.position || { x: 0, y: 0 };
      const normalizedTotWindGenCol = normalizeOptionalDerivedColumn(totWindGenCol, DEFAULT_WIND_COL);
      const normalizedTotSolarGenCol = normalizeOptionalDerivedColumn(totSolarGenCol, DEFAULT_SOLAR_COL);
      const normalizedTConsCol = normalizeOptionalDerivedColumn(tConsCol, DEFAULT_CONS_COL);
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // Make the API call if we have a valid pipeline name and input node
      if (session.pipeline_name !== "Unsaved_Pipeline" && inputNodeName) {
        try {
          // Determine which API to call based on whether we're reconfiguring, editing, or creating
          if (isReconfiguring) {
            // If reconfiguring, call the reconfigure API
            console.log(`Calling API to reconfigure extrapolation node: ${nodeName} in pipeline: ${session.pipeline_name}`);
            console.log(`New input node: ${inputNodeName}`);
            
            await reconfigureNode(
              session.pipeline_name,
              nodeName,
              inputNodeName
            );
            
            console.log(`Successfully reconfigured extrapolation node ${nodeName} with new input ${inputNodeName}`);
          } else if (isEditing && initialData.name) {
            // If editing, call the edit API
            console.log(`Calling API to edit extrapolation node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New values - Years: ${years}, Bankable Years: ${bankableYears}, Wind Dep: ${windDepPerc}%, Solar Dep: ${solarDepPerc}%, Banking Charge: ${bankingCharge}%`);
            
            await editExtrapolationNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              years,
              bankableYears,
              windDepPerc,
              solarDepPerc,
              bankingCharge,
              normalizedTotWindGenCol || null,
              normalizedTotSolarGenCol || null,
              consAppPerc,
              normalizedTConsCol || null
            );
            
            console.log(`Successfully edited extrapolation node ${initialData.name}`);
          } else {
            // If creating a new node, call the append API
            console.log(`Calling API to append extrapolation node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node: ${inputNodeName}, Years: ${years}, Bankable Years: ${bankableYears}, Wind Dep: ${windDepPerc}%, Solar Dep: ${solarDepPerc}%, Banking Charge: ${bankingCharge}%, Position: (${position.x}, ${position.y})`);
            
            await appendExtrapolationNodeToPipeline(
              session.pipeline_name,
              nodeName,
              years,
              bankableYears,
              inputNodeName,
              windDepPerc,
              solarDepPerc,
              bankingCharge,
              normalizedTotWindGenCol || '',
              normalizedTotSolarGenCol || '',
              consAppPerc,
              normalizedTConsCol || '',
              position.x,
              position.y
            );
            
            console.log(`Successfully added extrapolation node ${nodeName} to backend`);
          }
        } catch (apiError) {
          console.error('Error calling extrapolation node API:', apiError);
          const op = isReconfiguring ? OP.node.reconfigure : isEditing ? OP.node.editExtrapolation : OP.node.appendExtrapolation;
          const info = notifyError(apiError, { operation: op, showToast: false });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        years: years,
        bankableYears: bankableYears,
        windDepPerc: windDepPerc,
        solarDepPerc: solarDepPerc,
        bankingCharge: bankingCharge,
        totWindGenCol: normalizedTotWindGenCol,
        totSolarGenCol: normalizedTotSolarGenCol,
        consAppPerc: consAppPerc,
        tConsCol: normalizedTConsCol
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring extrapolation node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onClose({
        name: nodeName,
        years,
        bankableYears,
        windDepPerc,
        solarDepPerc,
        bankingCharge,
        totWindGenCol,
        totSolarGenCol,
        consAppPerc,
        tConsCol
      })}
      style={customStyles}
      contentLabel="Extrapolation Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="extrapolation-modal">
        <button onClick={() => onClose({
          name: nodeName,
          years,
          bankableYears,
          windDepPerc,
          solarDepPerc,
          bankingCharge,
          totWindGenCol,
          totSolarGenCol,
          consAppPerc,
          tConsCol
        })} className="extrapolation-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="extrapolation-modal-title">
          {isEditing 
            ? 'Edit Extrapolation Node' 
            : 'Configure Extrapolation Node'}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="extrapolation-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="extrapolation-modal-field">
          <label className="extrapolation-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="extrapolation-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`extrapolation-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="extrapolation-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="extrapolation-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '20px' }}>
          <div className="extrapolation-modal-field" style={{ flex: 1 }}>
            <label className="extrapolation-modal-label">
              Years to Extrapolate:
            </label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              className="extrapolation-modal-input"
              placeholder="Years to extrapolate"
              step="1"
              min="1"
            />
          </div>

          <div className="extrapolation-modal-field" style={{ flex: 1 }}>
            <label className="extrapolation-modal-label">
              Bankable Years:
            </label>
            <input
              type="number"
              value={bankableYears}
              onChange={(e) => setBankableYears(e.target.value)}
              className="extrapolation-modal-input"
              placeholder="Bankable years"
              step="1"
              min="0"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '20px' }}>
          <div className="extrapolation-modal-field" style={{ flex: 1 }}>
            <label className="extrapolation-modal-label">
              Wind Depreciation (%):
            </label>
            <input
              type="number"
              value={windDepPerc}
              onChange={(e) => setWindDepPerc(e.target.value)}
              className="extrapolation-modal-input"
              placeholder="Wind depreciation %"
              step="0.1"
              min="0"
            />
          </div>

          <div className="extrapolation-modal-field" style={{ flex: 1 }}>
            <label className="extrapolation-modal-label">
              Solar Depreciation (%):
            </label>
            <input
              type="number"
              value={solarDepPerc}
              onChange={(e) => setSolarDepPerc(e.target.value)}
              className="extrapolation-modal-input"
              placeholder="Solar depreciation %"
              step="0.1"
              min="0"
            />
          </div>
        </div>

        <div className="extrapolation-modal-field">
          <label className="extrapolation-modal-label">
            Banking Charge (%):
          </label>
          <input
            type="number"
            value={bankingCharge}
            onChange={(e) => setBankingCharge(e.target.value)}
            className="extrapolation-modal-input"
            placeholder="Banking charge %"
            step="0.1"
            min="0"
          />
        </div>

        <div className="extrapolation-modal-field">
          <label className="extrapolation-modal-label">
            Total Wind Generation Column:
          </label>
          <input
            type="text"
            value={totWindGenCol}
            onChange={(e) => setTotWindGenCol(e.target.value)}
            className="extrapolation-modal-input"
            placeholder="Leave blank to auto-detect"
          />
        </div>

        <div className="extrapolation-modal-field">
          <label className="extrapolation-modal-label">
            Total Solar Generation Column:
          </label>
          <input
            type="text"
            value={totSolarGenCol}
            onChange={(e) => setTotSolarGenCol(e.target.value)}
            className="extrapolation-modal-input"
            placeholder="Leave blank to auto-detect"
          />
        </div>

        <div className="extrapolation-modal-field">
          <label className="extrapolation-modal-label">
            Consumption Appreciation (%):
          </label>
          <input
            type="number"
            value={consAppPerc}
            onChange={(e) => setConsAppPerc(e.target.value)}
            className="extrapolation-modal-input"
            placeholder="Consumption appreciation %"
            step="0.1"
            min="0"
          />
        </div>

        <div className="extrapolation-modal-field">
          <label className="extrapolation-modal-label">
            Total Consumption Column:
          </label>
          <input
            type="text"
            value={tConsCol}
            onChange={(e) => setTConsCol(e.target.value)}
            className="extrapolation-modal-input"
            placeholder="Leave blank to auto-detect"
          />
        </div>
        
        <div className="extrapolation-modal-button-container">
          <button 
            className="extrapolation-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              !years || isNaN(years) || parseInt(years) <= 0 ||
              !bankableYears || isNaN(bankableYears) || parseInt(bankableYears) < 0
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              !years || isNaN(years) || parseInt(years) <= 0 ? 'Valid years value is required' : 
              !bankableYears || isNaN(bankableYears) || parseInt(bankableYears) < 0 ? 'Valid bankable years value is required' : ''
            }
          >
            {isSubmitting 
              ? (isEditing ? 'Saving...' : 'Configuring...') 
              : (isEditing ? 'Save Changes' : 'Configure')
            }
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ExtrapolationConfigModal;